<template>
    <main role="main" class="sd-v2">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('statistics.dashboard.title') }}</div>
        </portal>

        <header class="sd-hero">
            <div class="sd-hero-icon">
                <i class="fal fa-chart-pie"></i>
            </div>
            <div class="sd-hero-body">
                <h1 class="sd-hero-title">{{ $t('statistics.dashboard.title') }}</h1>
                <p class="sd-hero-sub">
                    <span>{{ $t('statistics.dashboard.subtitle') }}</span>
                    <template v-if="config && config.is_default">
                        <span class="sd-hero-sub-sep" aria-hidden="true">·</span>
                        <span class="sd-hero-sub-pill">
                            {{ $t('statistics.dashboard.default_config') }}
                        </span>
                    </template>
                </p>
            </div>
            <div class="sd-hero-actions">
                <PeriodSelector :value="period" @input="onPeriodChange" />
                <router-link
                    v-if="isAdmin"
                    to="/statistiques/config"
                    class="sd-config-btn"
                    :title="$t('statistics.dashboard.configure')"
                >
                    <i class="fal fa-cog"></i>
                </router-link>
            </div>
        </header>

        <div v-if="loading && !dashboard" class="sd-loading">
            <i class="fal fa-spinner-third fa-spin"></i>
        </div>

        <div v-else-if="error" class="sd-error">
            <i class="fal fa-exclamation-triangle"></i>
            <h3>{{ $t('statistics.dashboard.error_title') }}</h3>
            <p>{{ $t('statistics.dashboard.error_desc') }}</p>
            <button type="button" class="sd-error-retry" @click="reload">
                <i class="fal fa-redo"></i>
                {{ $t('statistics.dashboard.retry') }}
            </button>
        </div>

        <div v-else-if="!ids.length" class="sd-empty">
            <i class="fal fa-chart-line"></i>
            <h3>{{ $t('statistics.dashboard.empty_title') }}</h3>
            <p>{{ $t('statistics.dashboard.empty_desc') }}</p>
        </div>

        <div v-else class="sd-grid" :class="{ 'is-refreshing': loading }">
            <MetricWidget
                v-for="id in ids"
                :key="id"
                :class="cellClass(id)"
                :meta="metaFor(id)"
                :value="valueFor(id)"
            />
        </div>
    </main>
</template>

<script>
import { mapState } from 'vuex'
import MetricWidget from '@/components/Statistics/MetricWidget.vue'
import PeriodSelector from '@/components/Statistics/PeriodSelector.vue'

export default {
    name: 'StatisticsDashboard',
    components: { MetricWidget, PeriodSelector },
    computed: {
        ...mapState('statistics', {
            catalog: (s) => s.catalog,
            config: (s) => s.config,
            dashboard: (s) => s.dashboard,
            period: (s) => s.period,
            loading: (s) => s.loading,
            error: (s) => s.error
        }),
        catalogById() {
            const map = {}
            ;(this.catalog || []).forEach((m) => { map[m.id] = m })
            return map
        },
        ids() {
            return (this.dashboard && this.dashboard.ids) || []
        },
        isAdmin() {
            const role = JSON.parse(localStorage.getItem('role') || 'null')
            const roleName = role && typeof role === 'object' ? role.name : role
            return roleName === 'admin' || roleName === 'super-admin'
        }
    },
    created() {
        if (!this.period) {
            const today = new Date()
            const from = new Date(today)
            from.setDate(from.getDate() - 29)
            this.$store.commit('statistics/SET_PERIOD', {
                from: from.toISOString().slice(0, 10),
                to: today.toISOString().slice(0, 10)
            })
        }
        this.bootstrap()
    },
    methods: {
        bootstrap() {
            this.$store.dispatch('statistics/fetchCatalog').catch(() => {})
            this.$store.dispatch('statistics/fetchConfig').catch(() => {})
            this.$store.dispatch('statistics/fetchDashboard').catch(() => {})
        },
        onPeriodChange(period) {
            this.$store.dispatch('statistics/setPeriod', period)
            this.$store.dispatch('statistics/fetchDashboard').catch(() => {})
        },
        reload() {
            this.$store.dispatch('statistics/fetchDashboard', { force: true }).catch(() => {})
        },
        metaFor(id) {
            return this.catalogById[id] || { id, label: id, description: '', display: 'card', unit: 'count' }
        },
        valueFor(id) {
            return this.dashboard && this.dashboard.metrics ? this.dashboard.metrics[id] : null
        },
        cellClass(id) {
            const display = (this.metaFor(id).display || 'card')
            if (display === 'bar' || display === 'line' || display === 'area') {
                return 'sd-cell--full'
            }
            if (display === 'pie' || display === 'gauge') {
                return 'sd-cell--half'
            }
            return 'sd-cell--quarter'
        }
    }
}
</script>

<style scoped>
.sd-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.sd-hero {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-5);
    flex-wrap: wrap;
}

.sd-hero-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--v2-radius-lg);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.sd-hero-icon i {
    color: inherit !important;
}

.sd-hero-body {
    flex: 1;
    min-width: 200px;
}

.sd-hero-title {
    margin: 0 0 2px;
    font-size: 1.35rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.sd-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.sd-hero-sub-sep {
    color: var(--v2-text-4);
}

.sd-hero-sub-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    background: var(--v2-surface-2);
    color: var(--v2-text-2);
}

.sd-hero-actions {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.sd-config-btn {
    width: 36px;
    height: 36px;
    border-radius: var(--v2-radius-sm);
    border: 1px solid var(--v2-border);
    background: var(--v2-surface-0);
    color: var(--v2-text-2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none !important;
    transition: background var(--v2-transition-fast), color var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.sd-config-btn:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.sd-config-btn i {
    color: inherit !important;
    font-size: 0.95rem;
}

.sd-grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: var(--v2-space-4);
    transition: opacity var(--v2-transition-base);
}

.sd-grid.is-refreshing {
    opacity: 0.55;
    pointer-events: none;
}

.sd-cell--quarter {
    grid-column: span 3;
}

.sd-cell--half {
    grid-column: span 6;
}

.sd-cell--full {
    grid-column: span 12;
}

.sd-loading {
    display: flex;
    justify-content: center;
    padding: var(--v2-space-6);
    color: var(--v2-text-3);
    font-size: 1.5rem;
}

.sd-error,
.sd-empty {
    text-align: center;
    padding: 56px 24px;
    background: var(--v2-surface-0);
    border: 1px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.sd-error i,
.sd-empty i {
    font-size: 2.4rem;
    display: block;
    margin-bottom: 12px;
}

.sd-error i {
    color: var(--v2-danger, #dc2626);
}

.sd-empty i {
    color: var(--v2-text-4);
}

.sd-error h3,
.sd-empty h3 {
    margin: 0 0 6px;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
}

.sd-error p,
.sd-empty p {
    margin: 0 auto;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    max-width: 360px;
    line-height: 1.5;
}

.sd-error-retry {
    margin-top: 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid var(--v2-border);
    background: var(--v2-surface-0);
    border-radius: var(--v2-radius-sm);
    color: var(--v2-text-2);
    font: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    transition: background var(--v2-transition-fast), color var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.sd-error-retry:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

@media (max-width: 1024px) {
    .sd-cell--quarter { grid-column: span 6; }
    .sd-cell--half { grid-column: span 12; }
}

@media (max-width: 640px) {
    .sd-v2 { padding: 18px 16px 32px; }
    .sd-cell--quarter { grid-column: span 12; }
    .sd-hero-actions { flex-basis: 100%; }
}
</style>
